import React, { useRef, useState } from 'react';
import { ButtonContainer, ConfirmWithCommentPayload, StyledEditDialog, StyledSmartLabel } from './ConfirmWithCommentDialog';
import { HelpBox } from '../help-boxes';
import { CheckBoxPanel } from 'styles/v2/Styles.styled';
import { SecondaryButton, PrimaryButton } from '../buttons';
import { DepublishReasonType } from 'types/types';
import MyRadioButton from 'components/controls/MyRadioButton';
import TextareaWithMaxRows from './TextareaWithMaxRows';
import { COMMENT_MAX_LENGTH } from 'constants/constants';
import styled from 'styled-components';
interface EPDDetails {
  title?: string;
  fullIdentificationNumber?: string;
}

export interface DepublishWithReasonSelectionPayload extends ConfirmWithCommentPayload {
  depublishType: DepublishReasonType | null;
}

type Props = {
  epd: EPDDetails | null | undefined;
  isOpened: boolean;
  onClose(payload: DepublishWithReasonSelectionPayload | null): void;
};

const DepublishAdminDialog: React.FC<Props> = ({ epd, isOpened, onClose }) => {
  const [commentText, setCommentText] = React.useState<string>('');
  const [isCommentTouched, setCommentTouched] = useState<boolean>(false);
  const [depublishType, setDepublishType] = useState<DepublishReasonType | null>(null);
  const isDismissingRef = useRef<boolean>(false);
  const haveError = false;

  const handleDismiss = () => {
    isDismissingRef.current = true;
    onClose(null);
  };

  const handlePrimaryButtonClick = () => {
    if (haveError) {
      setCommentTouched(true);
      return;
    }

    isDismissingRef.current = false;
    onClose({ depublishType, commentText });
  };

  return (
    <StyledEditDialog
      header={'Depublish EPD'}
      isOpened={isOpened}
      onHide={handleDismiss}
      transitionOptions={
        {
          onExited() {
            // Reset state only after closing animation has finished to avoid noticeable flickering
            setCommentTouched(false);
            if (!isDismissingRef.current) {
              setCommentText('');
            }
          },
        } as any
      }
      closable={false}
    >
      {
        <>
          <HelpBox>
            <span>Please specify the reason for depublishing</span>
            <b>
              {epd?.fullIdentificationNumber} {epd?.title}
            </b>
            <span>The EPD Owner will be informed via email about the reason for depublishing the EPD.</span>
          </HelpBox>
          <RadioButtonPanel>
            <MyRadioButton
              inputId={'depublishedEPDNonCompliant'}
              value={DepublishReasonType.DepublishedByAdminNonCompliant}
              name="editMode"
              checked={depublishType === DepublishReasonType.DepublishedByAdminNonCompliant}
              onChange={(value) => setDepublishType(value)}
            />
            <label htmlFor={'depublishedEPDNonCompliant'} style={{ textTransform: 'none', marginLeft: '0.5rem' }}>
              EPD is non-compliant.
            </label>
          </RadioButtonPanel>
          <RadioButtonPanel style={{ marginTop: '0.5rem' }}>
            <MyRadioButton
              inputId={'depublishedEPDNotPaid'}
              value={DepublishReasonType.DepublishedByAdminEPDNotPaid}
              name="editMode"
              checked={depublishType === DepublishReasonType.DepublishedByAdminEPDNotPaid}
              onChange={(value) => setDepublishType(value)}
            />
            <label htmlFor={'depublishedEPDNotPaid'} style={{ textTransform: 'none', marginLeft: '0.5rem' }}>
              EPD registration or annual fee invoice was not paid.
            </label>
          </RadioButtonPanel>
        </>
      }
      <StyledSmartLabel
        htmlFor="confirmDialogComment"
        label={'Depublishing comment'}
        error={haveError ? 'Required' : undefined}
      />
      <TextareaWithMaxRows
        id="confirmDialogComment"
        value={commentText}
        onChange={(e) => setCommentText(e.target.value.slice(0, COMMENT_MAX_LENGTH))}
        onBlur={() => setCommentTouched(true)}
        style={{ width: '100%' }}
        rows={8}
        placeholder="Type here"
        maxRows={8}
        maxLength={COMMENT_MAX_LENGTH}
        autoResize
      />
      <ButtonContainer>
        <SecondaryButton label="Cancel" onClick={handleDismiss} />
        <PrimaryButton
          label={'Depublish'}
          onClick={handlePrimaryButtonClick}
          disabled={(haveError && isCommentTouched) || !depublishType}
        />
      </ButtonContainer>
    </StyledEditDialog>
  );
};

const RadioButtonPanel = styled(CheckBoxPanel)`
  font-size: 14px;
  color: ${(props) => props.theme.colors.primaryBlack};
`;

export default DepublishAdminDialog;
